import { Box, Chip, Typography, Fade, Stack, Divider } from "@mui/material";
import MinimalistCard from "../../../components/MinimalistCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import WarningIcon from "@mui/icons-material/Warning";
import HelpIcon from "@mui/icons-material/Help";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import StarIcon from "@mui/icons-material/Star";

export default function OrderStatus({ data, isSmall, isPriority }) {
  const formatDate = (dateString) => {
    if (!dateString) return "No disponible";

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      // Estados completados/entregados (verde)
      "Completado": { color: "#229954", icon: <CheckCircleIcon /> },
      "completado": { color: "#229954", icon: <CheckCircleIcon /> },
      "Entregado": { color: "#2ECC71", icon: <CheckCircleIcon /> },
      "entregado": { color: "#2ECC71", icon: <CheckCircleIcon /> },
      "ENTREGADO": { color: "#2ECC71", icon: <CheckCircleIcon /> },
      "COMPLETADO": { color: "#229954", icon: <CheckCircleIcon /> },
      "Documentación rectificada": { color: "#27AE60", icon: <DescriptionIcon /> },
      "DOCUMENTACIÓN RECTIFICADA": { color: "#27AE60", icon: <DescriptionIcon /> },
      
      // Estados en procesamiento normal (azul)
      "En Procesando": { color: "#3498DB", icon: <AccessTimeIcon /> },
      "en proceso": { color: "#3498DB", icon: <AccessTimeIcon /> },
      "En Proceso": { color: "#3498DB", icon: <AccessTimeIcon /> },
      "enviado": { color: "#3498DB", icon: <LocalShippingIcon /> },
      "Awaiting SEUR shipment": { color: "#34495E", icon: <LocalMallIcon /> },
      "Awaiting SEUR Shipment": { color: "#34495E", icon: <LocalMallIcon /> },
      "Awaiting SEUR Label": { color: "#34495E", icon: <LocalMallIcon /> },
      "Awaiting SEUR En Procesando": { color: "#34495E", icon: <LocalMallIcon /> },
      "EN TRÁNSITO": { color: "#2980B9", icon: <LocalShippingOutlinedIcon /> },
      "Disponible para recoger en tienda": { color: "#1ABC9C", icon: <LocationOnIcon /> },
      "DISPONIBLE PARA RECOGER EN TIENDA": { color: "#1ABC9C", icon: <LocationOnIcon /> },
      
      // Estados pendientes/espera (amarillo/naranja)
      "preparando": { color: "#F1C40F", icon: <AccessTimeIcon /> },
      "pendiente": { color: "#F1C40F", icon: <AccessTimeIcon /> },
      "Pendiente": { color: "#F1C40F", icon: <AccessTimeIcon /> },
      "Pendiente de pago": { color: "#F1C40F", icon: <AccessTimeIcon /> },
      "En espera": { color: "#F39C12", icon: <AccessTimeIcon /> },
      "Borrador": { color: "#D5DBDB", icon: <EditIcon /> },
      
      // Estados con problemas (rojo/naranja fuerte)
      "DEVOLUCIÓN EN CURSO": { color: "#E67E22", icon: <ReportProblemIcon /> },
      "APORTAR SOLUCIÓN": { color: "#D35400", icon: <HelpIcon /> },
      "Aportar solución": { color: "#D35400", icon: <HelpIcon /> },
      "INCIDENCIA": { color: "#D35400", icon: <ReportProblemIcon /> },
      "Incidencia": { color: "#D35400", icon: <ReportProblemIcon /> },
      "cancelado": { color: "#E74C3C", icon: <CancelIcon /> },
      "Cancelado": { color: "#E74C3C", icon: <CancelIcon /> },
      "Reembolsado": { color: "#C0392B", icon: <CreditCardIcon /> },
      "Fallido": { color: "#922B21", icon: <WarningIcon /> }
    };
  
    // Normalizamos el status (si es null o undefined, asignamos "pendiente")
    const normalizedStatus = status || "Borrador";
    // Devolvemos el valor del mapa o un valor predeterminado si no existe
    return statusMap[normalizedStatus] || { color: "#95A5A6", icon: <FiberManualRecordIcon /> };
  };


  const statusInfo = getStatusInfo(data?.status);

  return (
    <MinimalistCard>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          Estado del Pedido
        </Typography>
        {isPriority && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <StarIcon sx={{ color: '#f9de4b', mr: 0.5 }} />
            <Typography variant="body2" fontWeight="bold" color="#757575">
              Prioritario
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant={isSmall ? "body2" : "body1"}>
            <strong>Estado actual:</strong>
          </Typography>
          <Chip
            label={data?.status || "N/A"}
            size="small"
            sx={{
              color: "white",
              backgroundColor: statusInfo.color,
              fontWeight: "bold",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant={isSmall ? "body2" : "body1"}>
            <strong>Origen:</strong>
          </Typography>
          <Typography variant={isSmall ? "body2" : "body1"}>
            {data?.origen || "N/A"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant={isSmall ? "body2" : "body1"}>
            <strong>Costo de envío:</strong>
          </Typography>
          <Typography variant={isSmall ? "body2" : "body1"}>
            {data?.shipping_cost || "0.00"} €
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant={isSmall ? "body2" : "body1"}>
            <strong>Descuento:</strong>
          </Typography>
          <Typography variant={isSmall ? "body2" : "body1"}>
            {data?.discount || 0} €
          </Typography>
        </Box>

    

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant={isSmall ? "body2" : "body1"}>
            <strong>Fecha de creación:</strong>
          </Typography>
          <Typography variant={isSmall ? "body2" : "body1"}>
            {formatDate(data?.createdAt)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant={isSmall ? "body2" : "body1"} fontWeight="bold">
            Total:
          </Typography>
          <Typography variant={isSmall ? "body2" : "body1"} fontWeight="bold">
            {data?.total_cost || "0.00"} €
          </Typography>
        </Box>
      </Box>
    </MinimalistCard>
  );
}
